import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, BarChart3, TrendingUp, Users, Pizza } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/api';

const DataExtraction = ({ stores }) => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    store_id: 'all',
    start_date: '',
    end_date: '',
    report_type: 'summary'
  });

  const [dashboardData, setDashboardData] = useState(null);

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.store_id !== 'all') {
        params.store_id = filters.store_id;
      }
      if (filters.start_date) {
        params.start_date = filters.start_date;
      }
      if (filters.end_date) {
        params.end_date = filters.end_date;
      }

      let endpoint = '';
      switch (filters.report_type) {
        case 'summary':
          endpoint = '/analytics/dashboard/summary';
          break;
        case 'revenue':
          params.group_by = 'month';
          endpoint = '/analytics/dashboard/revenue';
          break;
        case 'menu':
          endpoint = '/analytics/dashboard/menu-performance';
          break;
        case 'customers':
          endpoint = '/analytics/dashboard/customers';
          break;
        default:
          endpoint = '/analytics/dashboard/summary';
      }

      const response = await apiClient.get(endpoint, { params });
      setDashboardData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.store_id !== 'all') {
        params.store_id = filters.store_id;
      }
      if (filters.start_date) {
        params.start_date = filters.start_date;
      }
      if (filters.end_date) {
        params.end_date = filters.end_date;
      }

      const response = await apiClient.get('/analytics/export/orders-csv', {
        params,
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Data Extraction & Analytics</h2>
        <p className="text-gray-600">Generate reports and export data for analysis</p>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle>Report Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label>Store</Label>
              <Select value={filters.store_id} onValueChange={(value) => setFilters({ ...filters, store_id: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select store" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stores</SelectItem>
                  {stores.map((store) => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Report Type</Label>
              <Select value={filters.report_type} onValueChange={(value) => setFilters({ ...filters, report_type: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select report" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="summary">Dashboard Summary</SelectItem>
                  <SelectItem value="revenue">Revenue Analysis</SelectItem>
                  <SelectItem value="menu">Menu Performance</SelectItem>
                  <SelectItem value="customers">Customer Analytics</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              />
            </div>

            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              />
            </div>
          </div>

          <div className="flex gap-3 mt-4">
            <Button onClick={handleGenerateReport} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              <BarChart3 className="w-4 h-4 mr-2" />
              Generate Report
            </Button>
            <Button onClick={handleExportCSV} disabled={loading} variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {filters.report_type === 'summary' && dashboardData.summary && (
            <>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Orders</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{dashboardData.summary.total_orders}</div>
                  <p className="text-xs text-gray-500 mt-1">
                    {dashboardData.summary.completed_orders} completed
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">₹{dashboardData.summary.total_revenue.toLocaleString()}</div>
                  <p className="text-xs text-gray-500 mt-1">
                    Avg: ₹{dashboardData.summary.average_order_value.toFixed(0)}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Customers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{dashboardData.summary.unique_customers}</div>
                  <p className="text-xs text-gray-500 mt-1">Unique customers</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Pending Orders</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{dashboardData.summary.pending_orders}</div>
                  <p className="text-xs text-gray-500 mt-1">In progress</p>
                </CardContent>
              </Card>
            </>
          )}

          {filters.report_type === 'revenue' && dashboardData.data && (
            <Card className="col-span-full">
              <CardHeader>
                <CardTitle>Revenue Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-semibold border-b pb-2">
                    <span>Period</span>
                    <span>Revenue</span>
                    <span>Orders</span>
                  </div>
                  {dashboardData.data.slice(0, 10).map((item) => (
                    <div key={item.period} className="flex justify-between text-sm">
                      <span>{item.period}</span>
                      <span className="font-medium">₹{item.total_revenue.toLocaleString()}</span>
                      <span className="text-gray-600">{item.order_count}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between font-bold">
                    <span>Total</span>
                    <span>₹{dashboardData.total_revenue.toLocaleString()}</span>
                    <span>{dashboardData.total_orders} orders</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {filters.report_type === 'menu' && dashboardData.top_sellers && (
            <Card className="col-span-full">
              <CardHeader>
                <CardTitle>Top Selling Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-semibold border-b pb-2">
                    <span>Item Name</span>
                    <span>Quantity</span>
                    <span>Revenue</span>
                  </div>
                  {dashboardData.top_sellers.slice(0, 10).map((item) => (
                    <div key={item.name} className="flex justify-between text-sm">
                      <span className="flex-1">{item.name}</span>
                      <span className="w-20 text-right">{item.total_quantity}</span>
                      <span className="w-32 text-right font-medium">₹{item.total_revenue.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {filters.report_type === 'customers' && dashboardData.top_customers && (
            <>
              <Card className="col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Customer Stats</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-2xl font-bold">{dashboardData.total_customers}</div>
                      <p className="text-xs text-gray-500">Total Customers</p>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{dashboardData.repeat_customers}</div>
                      <p className="text-xs text-gray-500">Repeat Customers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="col-span-full">
                <CardHeader>
                  <CardTitle>Top Customers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm font-semibold border-b pb-2">
                      <span>Customer</span>
                      <span>Orders</span>
                      <span>Total Spent</span>
                    </div>
                    {dashboardData.top_customers.slice(0, 10).map((customer) => (
                      <div key={customer.phone} className="flex justify-between text-sm">
                        <span className="flex-1">
                          {customer.name} ({customer.phone})
                        </span>
                        <span className="w-20 text-right">{customer.order_count}</span>
                        <span className="w-32 text-right font-medium">₹{customer.total_spent.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {!dashboardData && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Select filters and click "Generate Report" to view data</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DataExtraction;
